import { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO, { generateLocalBusinessSchema, generateBreadcrumbSchema } from '@/components/SEO';
import { createContact } from '@/lib/api';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send, Loader2, ArrowUpRight } from 'lucide-react';
import { trackFormSubmit, trackBookCall } from '@/lib/gtm';
import { Link } from 'react-router-dom';

const contactInfo = [
  { icon: Phone, title: "Phone", value: "+91 98787 80298", href: "tel:9878780298" },
  { icon: Mail, title: "Email", value: "sayhi@wizbang.in", href: "mailto:sayhi@wizbang.in" },
  { icon: MapPin, title: "Studio", value: "India · Hybrid + Remote", href: null },
  { icon: Clock, title: "Hours", value: "Mon — Sat, 9AM – 7PM IST", href: null },
];

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createContact(formData);
      const filled = Object.values(formData).filter(Boolean).length;
      trackFormSubmit('contact_form', filled);
      toast.success('Message sent. We\'ll be back to you within one working day.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-white border focus:border-[#ff5a1f] text-[#0A0A0A] px-4 py-3.5 text-sm outline-none rounded-md transition-colors placeholder:text-[#8A8A8A]";

  return (
    <div className="min-h-screen" style={{ background: '#FAFAF7' }} data-testid="contact-page">
      <SEO
        title="Contact — Let's talk growth"
        description="Talk to Wizbang. Performance marketing, e-commerce, web, custom CRM, packaging and branding — under one studio. Email sayhi@wizbang.in or call +91 98787 80298."
        keywords="contact wizbang, creative media technology agency contact, marketing agency India inquiry, performance marketing contact"
        url="/contact"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            generateLocalBusinessSchema(),
            generateBreadcrumbSchema([
              { name: "Home", path: "/" },
              { name: "Contact", path: "/contact" }
            ])
          ]
        }}
      />
      <Navbar />

      <section className="pt-32 pb-12 relative">
        <div className="absolute inset-0 bg-gradient-radial opacity-60" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="eyebrow mb-6">Get in touch</div>
          <h1 className="font-syne text-5xl sm:text-6xl lg:text-7xl font-bold text-[#0A0A0A] mb-8 max-w-5xl tracking-tight leading-[0.95]">
            Tell us about <span className="font-editorial text-[#ff5a1f]">your brief.</span><br />
            We'll bring back a sharper one.
          </h1>
          <p className="text-[#3A3A3A] text-lg md:text-xl max-w-2xl leading-relaxed">
            Drop us a line. Most messages get a personal reply from a senior team member within one working day.
          </p>
        </div>
      </section>

      <section className="py-16" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-4">
              <h2 className="font-syne text-2xl font-bold text-[#0A0A0A] mb-8 tracking-tight">Reach us</h2>
              <div className="space-y-5 mb-10">
                {contactInfo.map((info, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-5 bg-white border rounded-xl"
                    style={{ borderColor: 'var(--border)' }}>
                    <div className="w-10 h-10 bg-[#ff5a1f]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-5 h-5 text-[#ff5a1f]" />
                    </div>
                    <div>
                      <p className="text-[#8A8A8A] text-xs uppercase tracking-wider mb-1">{info.title}</p>
                      {info.href ? (
                        <a href={info.href} className="text-[#0A0A0A] font-medium hover:text-[#ff5a1f] transition-colors">{info.value}</a>
                      ) : (
                        <span className="text-[#0A0A0A] font-medium">{info.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-6 dark-section rounded-2xl text-white">
                <p className="font-editorial text-[#ff5a1f] mb-2 text-base">In a hurry?</p>
                <h3 className="font-syne text-xl font-bold text-white mb-3 tracking-tight">Skip the form. Book directly.</h3>
                <p className="text-white/60 text-sm mb-5">Grab a slot that works for you. We'll come prepared.</p>
                <Link to="/book-appointment" onClick={() => trackBookCall('contact_sidebar')}
                  className="inline-flex items-center gap-2 bg-[#ff5a1f] text-white px-5 py-3 text-sm font-semibold rounded-full hover:bg-white hover:text-[#0A0A0A] transition-colors">
                  Book a call <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-8">
              <div className="bg-white border rounded-2xl p-8 lg:p-10" style={{ borderColor: 'var(--border)' }}>
                <h2 className="font-syne text-2xl font-bold text-[#0A0A0A] mb-2 tracking-tight">Send us a message</h2>
                <p className="text-[#5B5B5B] text-sm mb-8">The more you tell us upfront, the sharper our first reply.</p>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[#5B5B5B] text-sm mb-2 font-medium">Name *</label>
                      <input type="text" name="name" value={formData.name} onChange={handleChange} required
                        data-testid="contact-name-input" className={inputCls} placeholder="Your name"
                        style={{ borderColor: 'var(--border)' }} />
                    </div>
                    <div>
                      <label className="block text-[#5B5B5B] text-sm mb-2 font-medium">Email *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} required
                        data-testid="contact-email-input" className={inputCls} placeholder="your@email.com"
                        style={{ borderColor: 'var(--border)' }} />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[#5B5B5B] text-sm mb-2 font-medium">Phone</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                        data-testid="contact-phone-input" className={inputCls} placeholder="+91 ..."
                        style={{ borderColor: 'var(--border)' }} />
                    </div>
                    <div>
                      <label className="block text-[#5B5B5B] text-sm mb-2 font-medium">What's it about? *</label>
                      <input type="text" name="subject" value={formData.subject} onChange={handleChange} required
                        data-testid="contact-subject-input" className={inputCls} placeholder="New project / collab / question"
                        style={{ borderColor: 'var(--border)' }} />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#5B5B5B] text-sm mb-2 font-medium">Tell us more *</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} required rows={6}
                      data-testid="contact-message-input" className={`${inputCls} resize-none`}
                      style={{ borderColor: 'var(--border)' }}
                      placeholder="What are you building? What's getting in the way? What does success look like?" />
                  </div>

                  <button type="submit" disabled={loading} data-testid="contact-submit-btn"
                    className="bg-[#0A0A0A] text-white px-8 py-4 text-sm font-semibold rounded-full hover:bg-[#ff5a1f] transition-colors btn-press flex items-center gap-2 disabled:opacity-50">
                    {loading ? (<><Loader2 className="w-4 h-4 animate-spin" />Sending...</>) : (<><Send className="w-4 h-4" />Send message</>)}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
